import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Chip,
  IconButton,
  TablePagination,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  InputAdornment,
  TableSortLabel,
  Tooltip,
  Divider,
  Alert,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { 
  Edit as EditIcon, 
  Delete as DeleteIcon, 
  Add as AddIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  Favorite as FavoriteFilledIcon,
  FavoriteBorder as FavoriteBorderIcon,
  Close as CloseIcon,
  FilterList as FilterListIcon,
  FileDownload as FileDownloadIcon,
  FileUpload as FileUploadIcon,
  CloudDownload as CloudDownloadIcon
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from "react-i18next";
import { usePaginationWithUrl } from '../../../hooks/usePaginationWithUrl';

// ✅ Import services
import { 
  getProductsPaginated,
  deleteProduct, 
  downloadTemplate,
  exportProducts,
  validateImportFile,
  importProducts,
  getProductsByFilters
} from '../services/productService';
import { useDispatch, useSelector } from 'react-redux';
import ProductWishlistDialog from '../../../components/ProductWishlistDialog';

// ✅ Import Redux actions for caching
import { fetchProductCategories } from '../../../app/store/slices/productCategorySlice';
import { fetchSuppliers } from '../../../app/store/slices/supplierSlice';
import { fetchProductBrands } from '../../../app/store/slices/productBrandSlice';

const ProductsListPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  
  // ✅ Get cached data from Redux store
  const { categories: categoriesFromStore, loading: categoriesLoading } = useSelector(state => state.productCategories || { categories: [], loading: false });
  const { suppliers: suppliersFromStore, loading: suppliersLoading } = useSelector(state => state.suppliers || { suppliers: [], loading: false });
  const { brands: brandsFromStore, loading: brandsLoading } = useSelector(state => state.productBrands || { brands: [], loading: false });
  
  // ✅ Updated state for pagination
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // ✅ Search and filter state
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [supplierFilter, setSupplierFilter] = useState('');
  const [stockStatusFilter, setStockStatusFilter] = useState('');
  
  // ✅ Sorting state (backend sorting)
  const [sortBy, setSortBy] = useState('Name');
  const [sortDirection, setSortDirection] = useState('Asc');
  
  // ✅ Pagination state with URL sync
  const { page, pageSize: rowsPerPage, handlePageChange, handlePageSizeChange, resetPage } = usePaginationWithUrl({
    defaultPage: 0,
    defaultPageSize: 10,
    pageSizeOptions: [10, 20, 50, 100]
  });
  
  // ✅ View dialog state
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);

  // ✅ Wishlist dialog state
  const [wishlistProduct, setWishlistProduct] = useState(null);

  // ✅ Import/Export state
  const [importDialog, setImportDialog] = useState({
    open: false,
    file: null,
    fileName: '',
    loading: false,
    validating: false,
    validationResult: null,
    error: null
  });

  const [exportDialog, setExportDialog] = useState({
    open: false,
    includeInventory: false,
    loading: false
  });

  // ✅ NEW: Category, Brand, and Supplier filter state (apply-button flow)
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [selectedBrandId, setSelectedBrandId] = useState('');
  const [selectedSupplierId, setSelectedSupplierId] = useState('');
  const [filterApplied, setFilterApplied] = useState(false);
  
  // ✅ NEW: Prevent duplicate API calls
  const [isFetching, setIsFetching] = useState(false);
  
  // ✅ Use data from Redux store (no local state needed)
  const categories = categoriesFromStore;
  const brands = brandsFromStore;
  const suppliers = suppliersFromStore;

  // ✅ Fetch products with pagination and filters
  const fetchProducts = async () => {
    // Prevent duplicate concurrent calls
    if (isFetching) return;
    
    setIsFetching(true);
    setLoading(true);
    try {
      const response = await getProductsPaginated({
        pageNumber: page + 1, // Convert from 0-based to 1-based
        pageSize: rowsPerPage,
        searchTerm: debouncedSearchTerm || null,
        categoryId: categoryFilter || null,
        brandId: brandFilter || null,
        supplierId: supplierFilter || null,
        stockStatus: stockStatusFilter || null,
        sortBy: sortBy,
        sortDirection: sortDirection
      });
      
      
      // ✅ Handle paginated response structure - FIXED MAPPING
      let productData = [];
      let total = 0;
      
      if (response?.payload) {
        // ✅ Correct structure: response.payload.Items (capital I)
        productData = response.payload.Items || response.payload.items || [];
        total = response.payload.TotalItems || response.payload.totalItems || 0;
        
      } else if (response?.Items) {
        // Alternative structure
        productData = response.Items;
        total = response.TotalItems || 0;
        
      } else if (response?.items) {
        // Lowercase alternative
        productData = response.items;
        total = response.totalCount || response.totalItems || 0;
        
      } else if (Array.isArray(response)) {
        // Direct array
        productData = response;
        total = response.length;
        
      }
      
      // ✅ Map product data to match component expectations
      const mappedProducts = productData.map(product => ({
        Id: product.Id,
        Name: product.Name,
        SKU: product.SKU || product.BarCode, // Fallback to BarCode if SKU not present
        BarCode: product.BarCode,
        Description: product.Description,
        SalePrice: product.SalePrice,
        CostPrice: product.CostPrice,
        MinSalePrice: product.MinSalePrice,
        AveragePrice: product.AveragePrice,
        StockQuantity: product.StockQuantity,
        MinStockLevel: product.MinStockLevel,
        IsActive: product.IsActive,
        IsPromotionProduct: product.IsPromotionProduct,
        PromotionPercentage: product.PromotionPercentage,
        // ✅ Extract nested category name
        CategoryName: product.ProductCategory?.CategoryName || product.CategoryName,
        CategoryId: product.ProductCategoryId || product.CategoryId,
        // ✅ Extract nested supplier name
        SupplierName: product.Supplier?.Name || product.SupplierName,
        SupplierId: product.SupplierId,
        // ✅ Extract nested brand name (if available)
        BrandName: product.ProductBrand?.BrandName || product.BrandName,
        BrandId: product.ProductBrandId || product.BrandId,
        // Additional fields
        ModelNumber: product.ModelNumber,
        ImageUrl: product.ImageUrl,
        SyncToShopify: product.SyncToShopify,
        IsWishlistProduct: product.IsWishlistProduct,
        CreatedDate: product.CreatedDate,
        ModifiedDate: product.ModifiedDate
      }));
      
      setProducts(mappedProducts);
      setTotalCount(total);
      
    } catch (error) {
      console.error('❌ Error fetching products:', error);
      toast.error('Failed to load products');
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  // ✅ Fetch products when filters, sorting, or pagination changes
  useEffect(() => {
    if (!filterApplied) {
      fetchProducts();
    }
  }, [page, rowsPerPage, sortBy, sortDirection, debouncedSearchTerm, categoryFilter, brandFilter, supplierFilter, stockStatusFilter, filterApplied]);

  // ✅ Load categories, suppliers, brands from Redux store (cached)
  useEffect(() => {
    // Check if data exists in store, if not fetch from API
    if (categoriesFromStore.length === 0 && !categoriesLoading) {
      console.log('📦 Fetching categories from API and caching in Redux store');
      dispatch(fetchProductCategories());
    }
    
    if (suppliersFromStore.length === 0 && !suppliersLoading) {
      console.log('📦 Fetching suppliers from API and caching in Redux store');
      dispatch(fetchSuppliers());
    }
    
    if (brandsFromStore.length === 0 && !brandsLoading) {
      console.log('📦 Fetching brands from API and caching in Redux store');
      dispatch(fetchProductBrands());
    }
  }, []); // Only run once on mount

  // ✅ Debounced search - Update debouncedSearchTerm after 500ms delay
  useEffect(() => {
    const delayedSearch = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      // Reset to first page on search change
      if (searchTerm !== debouncedSearchTerm && page !== 0) {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(delayedSearch);
  }, [searchTerm]);

  // ✅ Handle sort change
  const handleSort = (property) => {
    const isAsc = sortBy === property && sortDirection === 'Asc';
    setSortDirection(isAsc ? 'Desc' : 'Asc');
    setSortBy(property);
    setPage(0); // Reset to first page when sorting
  };

  // ✅ Pagination handlers
  // ✅ Pagination handlers now provided by hook
  // (handlePageChange and handlePageSizeChange from usePaginationWithUrl)

  // ✅ Clear all filters
  const handleClearFilters = () => {
    setSearchTerm('');
    setCategoryFilter('');
    setBrandFilter('');
    setSupplierFilter('');
    setStockStatusFilter('');
    setSelectedCategoryId('');
    setSelectedBrandId('');
    setSelectedSupplierId('');
    setFilterApplied(false);
    resetPage();
  };

  // ✅ NEW: Handle category/supplier filter button click
  const handleApplyFilter = async () => {
    if (!selectedCategoryId && !selectedSupplierId && !selectedBrandId) {
      toast.warning(t('please_select_filter') || 'Please select at least one filter (Category, Brand, or Supplier)');
      return;
    }

    // Prevent duplicate calls
    if (isFetching) return;
    
    setIsFetching(true);
    setLoading(true);
    try {
      
      const response = await getProductsByFilters(
        selectedCategoryId || null,
        selectedSupplierId || null,
        selectedBrandId || null
      );
      
      
      // Handle response structure
      const productData = response?.payload || response || [];
      const mappedProducts = Array.isArray(productData) ? productData.map(product => ({
        Id: product.Id,
        Name: product.Name,
        SKU: product.SKU || product.BarCode,
        BarCode: product.BarCode,
        Description: product.Description,
        SalePrice: product.SalePrice,
        CostPrice: product.CostPrice,
        MinSalePrice: product.MinSalePrice,
        AveragePrice: product.AveragePrice,
        StockQuantity: product.StockQuantity,
        MinStockLevel: product.MinStockLevel,
        IsActive: product.IsActive,
        IsPromotionProduct: product.IsPromotionProduct,
        PromotionPercentage: product.PromotionPercentage,
        CategoryName: product.ProductCategory?.CategoryName || product.CategoryName,
        CategoryId: product.ProductCategoryId || product.CategoryId,
        SupplierName: product.Supplier?.Name || product.SupplierName,
        SupplierId: product.SupplierId,
        BrandName: product.ProductBrand?.BrandName || product.BrandName,
        BrandId: product.ProductBrandId || product.BrandId,
        ModelNumber: product.ModelNumber,
        ImageUrl: product.ImageUrl,
        SyncToShopify: product.SyncToShopify,
        IsWishlistProduct: product.IsWishlistProduct,
        CreatedDate: product.CreatedDate,
        ModifiedDate: product.ModifiedDate
      })) : [];
      
      setProducts(mappedProducts);
      setTotalCount(mappedProducts.length);
      setFilterApplied(true);
      resetPage();
      
      toast.success(t('filter_applied') || `Found ${mappedProducts.length} products`);
      
    } catch (error) {
      console.error('❌ Error applying filter:', error);
      toast.error(error.response?.data?.message || 'Failed to apply filter');
      setProducts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  // ✅ View action handler
  const handleView = (product, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    
    navigate(`/products/detail/${product.Id}`, {
      state: { productData: product, returnUrl: location.pathname + location.search }
    });
  };

  // ✅ Edit Handler
  const handleEdit = (product, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    navigate(`/products/edit/${product.Id}`, {
      state: { 
        productData: product,
        timestamp: Date.now(),
        returnUrl: location.pathname + location.search
      }
    });
  };

  // ✅ Add new product handler
  const handleAddNew = () => {
    navigate('/products/add');
  };

  // ✅ Delete handler
  const handleDelete = async (productId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }

    if (window.confirm('Are you sure you want to delete this product?')) {
      try {
        await deleteProduct(productId);
        toast.success('Product deleted successfully');
        
        // ✅ Refresh current page
        fetchProducts();
        
      } catch (error) {
        console.error('❌ Error deleting product:', error);
        toast.error('Failed to delete product');
      }
    }
  };

  // ✅ Refresh handler
  const handleRefresh = () => {
    fetchProducts();
  };

  // ✅ Add to wishlist handler
  const handleAddToWishlist = (product) => {
    if (product.IsWishlistProduct) return; // already in wishlist — no-op
    setWishlistProduct(product);
  };

  // ✅ Wishlist success handler
  const handleWishlistSuccess = (product) => {
    // Update IsWishlistProduct in local products state immediately
    setProducts(prev => prev.map(p =>
      p.Id === product.Id ? { ...p, IsWishlistProduct: true } : p
    ));
  };

  // ✅ Download Template Handler
  const handleDownloadTemplate = async () => {
    try {
      toast.info('Downloading template...');
      
      const blob = await downloadTemplate();
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Product_Import_Template_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Template downloaded successfully');
    } catch (error) {
      console.error('❌ Error downloading template:', error);
      toast.error(error.response?.data?.message || 'Failed to download template');
    }
  };

  // ✅ Open Export Dialog
  const handleOpenExportDialog = () => {
    setExportDialog({
      open: true,
      includeInventory: false,
      loading: false
    });
  };

  // ✅ Close Export Dialog
  const handleCloseExportDialog = () => {
    setExportDialog({
      open: false,
      includeInventory: false,
      loading: false
    });
  };

  // ✅ Export Products Handler
  const handleExportProducts = async () => {
    try {
      setExportDialog(prev => ({ ...prev, loading: true }));
      toast.info('Exporting products...');
      
      const blob = await exportProducts(exportDialog.includeInventory);
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      const fileName = exportDialog.includeInventory 
        ? `Products_with_Inventory_${new Date().toISOString().split('T')[0]}.xlsx`
        : `Products_${new Date().toISOString().split('T')[0]}.xlsx`;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
      toast.success('Products exported successfully');
      handleCloseExportDialog();
    } catch (error) {
      console.error('❌ Error exporting products:', error);
      toast.error(error.response?.data?.message || 'Failed to export products');
      setExportDialog(prev => ({ ...prev, loading: false }));
    }
  };

  // ✅ Open Import Dialog
  const handleOpenImportDialog = () => {
    setImportDialog({
      open: true,
      file: null,
      fileName: '',
      loading: false,
      validating: false,
      validationResult: null,
      error: null
    });
  };

  // ✅ Close Import Dialog
  const handleCloseImportDialog = () => {
    setImportDialog({
      open: false,
      file: null,
      fileName: '',
      loading: false,
      validating: false,
      validationResult: null,
      error: null
    });
  };

  // ✅ Handle File Selection
  const handleFileChange = async (event) => {
    const file = event.target.files?.[0];
    
    if (!file) return;

    const errors = [];
    
    if (!file.name.endsWith('.xlsx')) {
      errors.push('Only .xlsx files are supported');
    }
    
    if (file.size > 10 * 1024 * 1024) {
      errors.push('File size exceeds 10MB limit');
    }

    if (errors.length > 0) {
      setImportDialog(prev => ({
        ...prev,
        file: null,
        fileName: '',
        error: errors.join('. '),
        validationResult: null
      }));
      toast.error(errors[0]);
      return;
    }

    setImportDialog(prev => ({
      ...prev,
      file,
      fileName: file.name,
      error: null,
      validating: true,
      validationResult: null
    }));

    try {
      const result = await validateImportFile(file);
      
      
      setImportDialog(prev => ({
        ...prev,
        validating: false,
        validationResult: result
      }));

      if (result.success || result.status === 200) {
        toast.success('File validated successfully');
      } else {
        toast.warning(result.message || 'Validation completed with warnings');
      }
    } catch (error) {
      console.error('❌ Validation error:', error);
      const errorMessage = error.response?.data?.message || 'File validation failed';
      
      setImportDialog(prev => ({
        ...prev,
        validating: false,
        validationResult: null,
        error: errorMessage
      }));
      
      toast.error(errorMessage);
    }
  };

  // ✅ Import Products Handler
  const handleImportProducts = async () => {
    if (!importDialog.file) {
      toast.error('Please select a file first');
      return;
    }

    try {
      setImportDialog(prev => ({ ...prev, loading: true }));
      toast.info('Importing products... This may take a few minutes.');
      
      const result = await importProducts(importDialog.file);
      
      
      if (result.success || result.status === 200) {
        const payload = result.payload || result.data || {};
        const successCount = payload.successCount || 0;
        const errorCount = payload.errorCount || 0;
        
        toast.success(
          `Import completed! Success: ${successCount}, Errors: ${errorCount}`,
          { autoClose: 5000 }
        );
        
        fetchProducts();
        handleCloseImportDialog();
      } else {
        toast.error(result.message || 'Import failed');
        setImportDialog(prev => ({ ...prev, loading: false }));
      }
    } catch (error) {
      console.error('❌ Import error:', error);
      const errorMessage = error.response?.data?.message || 'Failed to import products';
      toast.error(errorMessage);
      setImportDialog(prev => ({ ...prev, loading: false }));
    }
  };

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      {/* ✅ Header */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        mb: 3,
        flexWrap: 'wrap',
        gap: 2
      }}>
        <Typography 
          variant="h4" 
          component="h1" 
          sx={{ 
            fontWeight: 600,
            color: 'primary.main'
          }}
        >
          {t("product_list")}
        </Typography>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Button
            variant="outlined"
            startIcon={<CloudDownloadIcon />}
            onClick={handleDownloadTemplate}
            sx={{ textTransform: 'none' }}
          >
            {t('download_template')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileDownloadIcon />}
            onClick={handleOpenExportDialog}
            sx={{ textTransform: 'none' }}
          >
            {t('export_products')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<FileUploadIcon />}
            onClick={handleOpenImportDialog}
            sx={{ textTransform: 'none' }}
          >
            {t('import_products')}
          </Button>
          <Button
            variant="outlined"
            onClick={handleRefresh}
            disabled={loading}
            sx={{ textTransform: 'none' }}
          >
            {t('refresh')}
          </Button>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
            sx={{ 
              minWidth: 140,
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 500
            }}
          >
            {t("add_product")}
          </Button>
        </Box>
      </Box>

      {/* ✅ Search and Filters */}
      <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
        {/* NEW: Category and Supplier Filter Section */}
        <Box sx={{ mb: 3, pb: 3, borderBottom: '1px solid', borderColor: 'divider' }}>
          <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600, color: 'primary.main' }}>
            {t('filter_by_category_supplier') || 'Filter by Category / Supplier'}
          </Typography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('category') || 'Category'}</InputLabel>
                <Select
                  value={selectedCategoryId}
                  label={t('category') || 'Category'}
                  onChange={(e) => setSelectedCategoryId(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>{t('all_categories') || 'All Categories'}</em>
                  </MenuItem>
                  {categories.map((category) => (
                    <MenuItem key={category.Id} value={category.Id}>
                      {category.CategoryName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('brand') || 'Brand'}</InputLabel>
                <Select
                  value={selectedBrandId}
                  label={t('brand') || 'Brand'}
                  onChange={(e) => setSelectedBrandId(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>{t('all_brands') || 'All Brands'}</em>
                  </MenuItem>
                  {brands.map((brand) => (
                    <MenuItem key={brand.Id} value={brand.Id}>
                      {brand.BrandName}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={4} md={4}>
              <FormControl fullWidth size="small">
                <InputLabel>{t('supplier') || 'Supplier'}</InputLabel>
                <Select
                  value={selectedSupplierId}
                  label={t('supplier') || 'Supplier'}
                  onChange={(e) => setSelectedSupplierId(e.target.value)}
                  sx={{ borderRadius: 2 }}
                >
                  <MenuItem value="">
                    <em>{t('all_suppliers') || 'All Suppliers'}</em>
                  </MenuItem>
                  {suppliers.map((supplier) => (
                    <MenuItem key={supplier.Id} value={supplier.Id}>
                      {supplier.Name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <Button
                variant="contained"
                fullWidth
                startIcon={<SearchIcon />}
                onClick={handleApplyFilter}
                disabled={!selectedCategoryId && !selectedSupplierId && !selectedBrandId}
                sx={{ 
                  height: 40,
                  borderRadius: 2,
                  textTransform: 'none',
                  fontWeight: 600
                }}
              >
                {t('apply_filter') || 'Apply Filter'}
              </Button>
            </Grid>
            
            <Grid item xs={12} sm={6} md={6}>
              <Button
                variant="outlined"
                fullWidth
                startIcon={<ClearIcon />}
                onClick={() => {
                  setSelectedCategoryId('');
                  setSelectedBrandId('');
                  setSelectedSupplierId('');
                  setFilterApplied(false);
                  resetPage();
                  fetchProducts();
                }}
                disabled={!selectedCategoryId && !selectedBrandId && !selectedSupplierId && !filterApplied}
                sx={{ 
                  height: 40,
                  borderRadius: 2,
                  textTransform: 'none'
                }}
              >
                {t('clear') || 'Clear'}
              </Button>
            </Grid>
          </Grid>
          {filterApplied && (
            <Alert severity="info" sx={{ mt: 2 }}>
              {t('filter_active') || 'Filter is active'} - 
              {selectedCategoryId && ` ${t('category')}: ${categories.find(c => c.Id === selectedCategoryId)?.CategoryName}`}
              {selectedCategoryId && selectedBrandId && ' | '}
              {selectedBrandId && ` ${t('brand') || 'Brand'}: ${brands.find(b => b.Id === selectedBrandId)?.BrandName}`}
              {(selectedCategoryId || selectedBrandId) && selectedSupplierId && ' | '}
              {selectedSupplierId && ` ${t('supplier')}: ${suppliers.find(s => s.Id === selectedSupplierId)?.Name}`}
            </Alert>
          )}
        </Box>
        
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t('search_by_name_sku_barcode')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>{t('stock_status')}</InputLabel>
              <Select
                value={stockStatusFilter}
                label={t('stock_status')}
                onChange={(e) => {
                  setStockStatusFilter(e.target.value);
                  setPage(0);
                }}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="">{t('all')}</MenuItem>
                <MenuItem value="InStock">{t('in_stock')}</MenuItem>
                <MenuItem value="LowStock">Low Stock</MenuItem>
                <MenuItem value="OutOfStock">Out of Stock</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} sm={6} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              {(searchTerm || categoryFilter || brandFilter || supplierFilter || stockStatusFilter) && (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={handleClearFilters}
                  startIcon={<ClearIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Clear Filters
                </Button>
              )}
              <Chip
                label={`${totalCount} ${t("products")}`}
                color="primary"
                variant="outlined"
                icon={<FilterListIcon />}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      {/* ✅ Products Table with Server-side Sorting */}
      <Paper elevation={2} sx={{ borderRadius: 2 }}>
        {loading ? (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center',
            py: 8 
          }}>
            <CircularProgress size={60} />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'Name'}
                        direction={sortBy === 'Name' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('Name')}
                      >
                        {t("product_name")}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'SKU'}
                        direction={sortBy === 'SKU' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('SKU')}
                      >
                        SKU
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("category")}
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'Price'}
                        direction={sortBy === 'Price' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('Price')}
                      >
                        {t("unit_price")}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      <TableSortLabel
                        active={sortBy === 'Stock'}
                        direction={sortBy === 'Stock' ? sortDirection.toLowerCase() : 'asc'}
                        onClick={() => handleSort('Stock')}
                      >
                        {t("stock")}
                      </TableSortLabel>
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }}>
                      {t("status")}
                    </TableCell>

                    <TableCell sx={{ fontWeight: 600 }} align="center">
                      {t("actions")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {products.map((product) => (
                    <TableRow key={product.Id} hover>
                      <TableCell>
                        <Box>
                          <Typography variant="body1" sx={{ fontWeight: 500 }}>
                            {product.Name}
                          </Typography>
                          {product.Description && (
                            <Typography variant="body2" color="text.secondary" noWrap>
                              {product.Description.length > 50 
                                ? `${product.Description.substring(0, 50)}...` 
                                : product.Description
                              }
                            </Typography>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                          {product.SKU}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {product.CategoryName ? (
                          <Chip
                            label={product.CategoryName}
                            size="small"
                            variant="outlined"
                            color="primary"
                            sx={{ borderRadius: 1 }}
                          />
                        ) : (
                          <Typography variant="body2" color="text.disabled">
                            {t("no_category")}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body1" sx={{ fontWeight: 500 }}>
                          €{product.SalePrice?.toFixed(2)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 500,
                              color: (product.StockQuantity || 0) <= (product.MinStockLevel || 0) 
                                ? 'error.main' 
                                : 'text.primary'
                            }}
                          >
                            {product.StockQuantity || 0}
                          </Typography>
                          
                          {product.MinStockLevel && (
                            <Typography variant="caption" color="text.secondary">
                              Min: {product.MinStockLevel}
                            </Typography>
                          )}
                          
                          {(product.StockQuantity || 0) <= (product.MinStockLevel || 0) && 
                           product.MinStockLevel > 0 && (
                            <Chip
                              label={t("low_stock")}
                              size="small"
                              color="error"
                              variant="outlined"
                              sx={{ ml: 1, fontSize: '0.7rem', height: 20 }}
                            />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={product.IsActive ? t("active") : t("inactive")} 
                          size="small" 
                          color={product.IsActive ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                          <Tooltip title={product.IsWishlistProduct ? t('in_wishlist') || 'In Wishlist' : t('add_to_wishlist')}>
                            <IconButton
                              size="small"
                              color="secondary"
                              onClick={() => handleAddToWishlist(product)}
                            >
                              {product.IsWishlistProduct
                                ? <FavoriteFilledIcon fontSize="small" />
                                : <FavoriteBorderIcon fontSize="small" />
                              }
                            </IconButton>
                          </Tooltip>
                          
                          <IconButton
                            size="small"
                            color="info"
                            onClick={(event) => handleView(product, event)}
                            title={t("view_product_details")}
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={(event) => handleEdit(product, event)}
                            title={t("edit_product")}
                          >
                            <EditIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={(event) => handleDelete(product.Id, event)}
                            title={t("delete_product")}
                          >
                            <DeleteIcon fontSize="small" />
                          </IconButton>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 20, 50, 100]}
              component="div"
              count={totalCount}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handlePageChange}
              onRowsPerPageChange={handlePageSizeChange}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider'
              }}
            />
          </>
        )}

        {/* ✅ Empty State */}
        {!loading && products.length === 0 && (
          <Box sx={{ 
            display: 'flex', 
            flexDirection: 'column',
            alignItems: 'center', 
            py: 8,
            gap: 2
          }}>
            <Typography variant="h6" color="text.secondary">
              {searchTerm || categoryFilter || brandFilter || supplierFilter || stockStatusFilter
                ? t("no_products_found")
                : t("no_products_available")}
            </Typography>
            {!searchTerm && !categoryFilter && !brandFilter && !supplierFilter && !stockStatusFilter && (
              <>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                  {t("get_started_by_adding_your_first_product")}
                </Typography>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={handleAddNew}
                  sx={{ textTransform: 'none' }}
                >
                  {t("add_first_product")}
                </Button>
              </>
            )}
            {(searchTerm || categoryFilter || brandFilter || supplierFilter || stockStatusFilter) && (
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                sx={{ textTransform: 'none' }}
              >
                {t("clear_filters")}
              </Button>
            )}
          </Box>
        )}
      </Paper>

      {/* ✅ View Product Dialog */}
      <Dialog 
        open={viewDialogOpen} 
        onClose={() => setViewDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>{t("product_details")}</DialogTitle>
        <DialogContent>
          {selectedProduct && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">{t("product_name")}</Typography>
                <Typography variant="body1">{selectedProduct.Name}</Typography>
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography variant="subtitle2" color="text.secondary">{t("sku")}</Typography>
                <Typography variant="body1">{selectedProduct.SKU}</Typography>
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setViewDialogOpen(false)}>{t("close")}</Button>
          <Button onClick={() => { setViewDialogOpen(false); handleEdit(selectedProduct); }} variant="contained">
            {t("edit")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Add to Wishlist Dialog */}
      <ProductWishlistDialog
        open={Boolean(wishlistProduct)}
        onClose={() => setWishlistProduct(null)}
        product={wishlistProduct}
        onSuccess={handleWishlistSuccess}
      />

      {/* ✅ Export Dialog */}
      <Dialog 
        open={exportDialog.open} 
        onClose={handleCloseExportDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileDownloadIcon color="primary" />
              <Typography variant="h6">Export Products</Typography>
            </Box>
            <IconButton onClick={handleCloseExportDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3 }}>
          <Box sx={{ mb: 3 }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              Export all products to an Excel file (.xlsx)
            </Typography>
            
            <FormControl component="fieldset" sx={{ mt: 2 }} size="small">
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <input
                  type="checkbox"
                  checked={exportDialog.includeInventory}
                  onChange={(e) => setExportDialog(prev => ({ ...prev, includeInventory: e.target.checked }))}
                  style={{ marginRight: '8px' }}
                />
                <Typography variant="body2">
                  Include inventory details (stock quantities, min stock levels)
                </Typography>
              </Box>
            </FormControl>

            <Alert severity="info" sx={{ mt: 3 }}>
              The exported file will contain all product data in Excel format.
            </Alert>
          </Box>
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseExportDialog}
            disabled={exportDialog.loading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleExportProducts}
            disabled={exportDialog.loading}
            startIcon={exportDialog.loading ? <CircularProgress size={20} /> : <FileDownloadIcon />}
          >
            {exportDialog.loading ? 'Exporting...' : 'Export'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Import Dialog */}
      <Dialog 
        open={importDialog.open} 
        onClose={handleCloseImportDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{ pb: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <FileUploadIcon color="primary" />
              <Typography variant="h6">Import Products</Typography>
            </Box>
            <IconButton onClick={handleCloseImportDialog} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        
        <Divider />
        
        <DialogContent sx={{ pt: 3 }}>
          {/* Instructions */}
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
              Import Instructions:
            </Typography>
            <Typography variant="body2" component="div">
              • Download the template first and fill in your product data<br />
              • Only .xlsx files are supported (max 10MB)<br />
              • Products with existing SKU will be updated<br />
              • Products without SKU will be created as new
            </Typography>
          </Alert>

          {/* File Upload Area */}
          <Box sx={{ 
            border: '2px dashed',
            borderColor: importDialog.file ? 'success.main' : 'grey.300',
            borderRadius: 2,
            p: 4,
            textAlign: 'center',
            bgcolor: importDialog.file ? 'success.50' : 'grey.50',
            mb: 3,
            transition: 'all 0.3s'
          }}>
            <input
              accept=".xlsx"
              style={{ display: 'none' }}
              id="import-file-upload"
              type="file"
              onChange={handleFileChange}
              disabled={importDialog.loading || importDialog.validating}
            />
            <label htmlFor="import-file-upload">
              <Button
                variant="outlined"
                component="span"
                startIcon={<FileUploadIcon />}
                disabled={importDialog.loading || importDialog.validating}
                sx={{ mb: 2 }}
              >
                Select File
              </Button>
            </label>
            
            {importDialog.fileName && (
              <Typography variant="body1" sx={{ fontWeight: 600, color: 'success.main' }}>
                📄 {importDialog.fileName}
              </Typography>
            )}
            
            {!importDialog.fileName && (
              <Typography variant="body2" color="text.secondary">
                Click to select an Excel file (.xlsx)
              </Typography>
            )}
          </Box>

          {/* Validation Status */}
          {importDialog.validating && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
              <CircularProgress size={24} />
              <Typography variant="body2">Validating file...</Typography>
            </Box>
          )}

          {/* Validation Result */}
          {importDialog.validationResult && (
            <Alert 
              severity={importDialog.validationResult.success ? "success" : "warning"} 
              sx={{ mb: 3 }}
            >
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {importDialog.validationResult.message || 'File validated'}
              </Typography>
              {importDialog.validationResult.payload && (
                <Box sx={{ mt: 1 }}>
                  <Typography variant="caption" component="div">
                    Details: {JSON.stringify(importDialog.validationResult.payload, null, 2)}
                  </Typography>
                </Box>
              )}
            </Alert>
          )}

          {/* Error Display */}
          {importDialog.error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {importDialog.error}
            </Alert>
          )}
        </DialogContent>
        
        <DialogActions sx={{ p: 3, pt: 1 }}>
          <Button 
            onClick={handleCloseImportDialog}
            disabled={importDialog.loading}
          >
            Cancel
          </Button>
          <Button 
            variant="contained"
            onClick={handleImportProducts}
            disabled={!importDialog.file || importDialog.loading || importDialog.validating || !!importDialog.error}
            startIcon={importDialog.loading ? <CircularProgress size={20} /> : <FileUploadIcon />}
          >
            {importDialog.loading ? 'Importing...' : 'Import Products'}
          </Button>
        </DialogActions>
      </Dialog>

    </Container>
  );
};

export default ProductsListPage;